'use client';

import { useEffect } from 'react';
import { useLocale } from 'next-intl';
import { localeDirection, type Locale } from '@/i18n/config';

/**
 * Keeps `<html lang>`/`<html dir>` in sync with the active locale on the client.
 *
 * `<html>` lives in the ROOT `app/layout.tsx` (so next-themes' flash-prevention
 * <script> hydrates once in the stable shell instead of being re-created on
 * navigation — see the comment there). The root sits above the `[locale]`
 * segment, so its `lang`/`dir` are only set during the initial server render and
 * go stale on a client-side locale switch (the header EN/ع toggle does a soft
 * `router.replace`). This mirrors the locale back onto `<html>` on every
 * navigation — the same pattern ActiveThemeProvider uses for `data-theme`. The
 * server render still sets the correct initial value, so there's no flash on load.
 */
export function HtmlDirSync() {
  const locale = useLocale();

  useEffect(() => {
    const root = document.documentElement;
    const dir = localeDirection[locale as Locale] ?? 'ltr';
    if (root.lang !== locale) root.lang = locale;
    if (root.dir !== dir) root.dir = dir;
  }, [locale]);

  return null;
}
